import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
}: DeleteAccountModalProps) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmed = confirmText === 'DELETE';

  const handleConfirm = async () => {
    if (!isConfirmed) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onConfirm();
      // onClose will be called by parent after successful deletion
    } catch (err) {
      setError(err instanceof Error ? err.message : t('deleteAccount.errors.failed'));
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmText('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('deleteAccount.title')}
    >
      <div className="p-6">
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <FiAlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Warning Message */}
        <div className="text-center mb-6">
          <p className="text-gray-700 mb-2">
            {t('deleteAccount.warning')}
          </p>
          <p className="text-sm text-gray-500">
            {t('deleteAccount.consequences')}
          </p>
        </div>

        {/* What will be deleted */}
        <div className="bg-red-50 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-red-800 mb-2">
            {t('deleteAccount.willDelete')}
          </p>
          <ul className="text-sm text-red-700 space-y-1">
            <li>&bull; {t('deleteAccount.items.profile')}</li>
            <li>&bull; {t('deleteAccount.items.photo')}</li>
            <li>&bull; {t('deleteAccount.items.account')}</li>
            <li>&bull; {t('deleteAccount.items.qrCode')}</li>
          </ul>
        </div>

        {/* Confirmation Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('deleteAccount.typeConfirm', { text: 'DELETE' })}
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="DELETE"
            disabled={isDeleting}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 disabled:opacity-50"
            autoComplete="off"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1"
          >
            {t('deleteAccount.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!isConfirmed || isDeleting}
            className="flex-1"
          >
            {isDeleting
              ? t('deleteAccount.deleting')
              : t('deleteAccount.confirmButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
